import { forwardRef, type ButtonHTMLAttributes, type ReactNode, type ElementType } from 'react';
import { cn } from '../../lib/utils';
import Spinner from './Spinner';

type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
type ButtonSize = 'sm' | 'md' | 'lg' | 'xl';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  as?: ElementType;
  to?: string;
  href?: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  fullWidth?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      as,
      variant = 'primary',
      size = 'md',
      isLoading = false,
      leftIcon,
      rightIcon,
      fullWidth = false,
      disabled,
      children,
      ...props
    },
    ref
  ) => {
    const baseStyles = `
      inline-flex items-center justify-center
      font-sans font-medium
      transition-all duration-300 ease-out
      focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-cream-50
      disabled:opacity-50 disabled:cursor-not-allowed
      relative overflow-hidden
    `;

    const variants: Record<ButtonVariant, string> = {
      primary: `
        bg-leather-600 text-cream-50
        hover:bg-leather-700
        focus:ring-leather-500
        shadow-soft hover:shadow-soft-lg
        active:scale-[0.98]
      `,
      secondary: `
        bg-gold-500 text-leather-900
        hover:bg-gold-600
        focus:ring-gold-400
        shadow-gold hover:shadow-lg
        active:scale-[0.98]
      `,
      outline: `
        bg-transparent text-leather-700
        border-2 border-leather-300
        hover:bg-leather-50 hover:border-leather-400
        focus:ring-leather-300
        active:scale-[0.98]
      `,
      ghost: `
        bg-transparent text-leather-600
        hover:bg-leather-100
        focus:ring-leather-200
        active:scale-[0.98]
      `,
      danger: `
        bg-error-500 text-white
        hover:bg-error-600
        focus:ring-error-400
        shadow-soft hover:shadow-soft-lg
        active:scale-[0.98]
      `,
    };

    const sizes: Record<ButtonSize, string> = {
      sm: 'px-3 py-1.5 text-sm rounded-lg gap-1.5',
      md: 'px-5 py-2.5 text-base rounded-xl gap-2',
      lg: 'px-7 py-3.5 text-lg rounded-xl gap-2.5',
      xl: 'px-9 py-4 text-xl rounded-2xl gap-3',
    };

    const Component = as || 'button';

    return (
      <Component
        ref={ref}
        className={cn(
          baseStyles,
          variants[variant],
          sizes[size],
          fullWidth && 'w-full',
          className
        )}
        disabled={Component === 'button' ? disabled || isLoading : undefined}
        {...props}
      >
        {isLoading ? (
          <>
            <Spinner size="sm" className="absolute" />
            <span className="opacity-0">{children}</span>
          </>
        ) : (
          <>
            {leftIcon && <span className="flex-shrink-0">{leftIcon}</span>}
            {children}
            {rightIcon && <span className="flex-shrink-0">{rightIcon}</span>}
          </>
        )}
      </Component>
    );
  }
);

Button.displayName = 'Button';

export { Button };
export default Button;
